import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { Navigate, NavLink, Outlet } from 'react-router-dom';

import LogoIcon from '../../../assets/images/Learnit_logo.svg';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import { fetchUser } from '../../../store/reducers/actionCreators';
import SidebarAccount from '../SidebarAccount';
import { searchOpsAccounts } from '../../../requests';
import {
  getPreviewAccount,
  setPreviewAccount,
  TPreviewAccount,
} from '../../../requests/customerPortal';

const GROUPS = [
  {
    label: 'Your training',
    items: [
      { to: PATH.adminPrograms, label: 'Programs & balances' },
      { to: PATH.adminEvents, label: 'Private events' },
      { to: PATH.adminPublicClasses, label: 'Public classes' },
      { to: PATH.adminRegistrations, label: 'Registrations' },
      { to: PATH.adminEvaluations, label: 'Evaluations' },
    ],
  },
  {
    label: 'Get help',
    items: [
      { to: PATH.adminRequestCourse, label: 'Request a private course' },
      { to: PATH.adminTickets, label: 'Tickets' },
    ],
  },
];

/**
 * Staff only: pick which customer's portal to look at. Managers never see
 * this — the API scopes them to their own company regardless.
 */
const PreviewPicker: React.FC = () => {
  const queryClient = useQueryClient();
  const [current, setCurrent] = useState<TPreviewAccount | null>(
    getPreviewAccount(),
  );
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  const { data: hits } = useQuery({
    queryKey: ['ops', 'accounts', 'search', q],
    queryFn: () => searchOpsAccounts(q),
    enabled: q.length >= 2,
  });
  const choose = (a: TPreviewAccount | null) => {
    setPreviewAccount(a);
    setCurrent(a);
    setSearch('');
    setQ('');
    queryClient.invalidateQueries({ queryKey: ['my'] });
  };
  return (
    <div className="relative flex flex-wrap items-center gap-3 border-b border-white-regular bg-white px-8 py-2.5 text-sm">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-two">
        Staff preview
      </span>
      {current ? (
        <>
          <span className="text-gray-five">
            Viewing as <b>{current.name}</b>
          </span>
          <button
            className="text-xs font-medium text-blue-primary hover:underline"
            onClick={() => choose(null)}
          >
            Clear
          </button>
        </>
      ) : (
        <span className="text-gray-bold">
          Pick a customer account to see their portal
        </span>
      )}
      <div className="relative ml-auto w-80">
        <input
          className="h-9 w-full rounded-lg border border-white-regular bg-white px-3 text-sm text-gray-four focus:border-blue-primary focus:outline-none"
          placeholder="Search accounts…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {q.length >= 2 && hits && (
          <div className="absolute right-0 top-10 z-40 max-h-72 w-full overflow-y-auto rounded-lg border border-white-regular bg-white shadow-lg">
            {hits.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-bold">No match</div>
            ) : (
              hits.slice(0, 20).map((a) => (
                <button
                  key={a.id}
                  className="block w-full border-b border-white-three px-3 py-2 text-left text-sm text-gray-four last:border-b-0 hover:bg-grey-50"
                  onClick={() => choose({ id: a.id, name: a.name })}
                >
                  {a.name}
                  {a.city && (
                    <span className="ml-2 text-xs text-gray-bold">
                      {a.city}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * The customer admin shell — what a client's training manager gets in
 * place of the Salesforce Community Plus "Admin Portal". Its own sidebar,
 * like the staff ops shell, but everything behind it is scoped to the
 * manager's company by the API. Managers and admins; the API enforces the
 * same rule.
 */
export const CustomerAdminLayout: React.FC = () => {
  const { user, isLoading } = useAppSelector((state) => state.userReducer);
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchUser());
  }, []);

  if (isLoading) {
    return null;
  }
  if (!user) {
    return <Navigate to={PATH.login} replace />;
  }
  if (user.role !== 'manager' && user.role !== 'admin') {
    return <Navigate to={PATH.upcomingClasses} replace />;
  }

  return (
    <div className="min-h-screen bg-grey-50 text-gray-four">
      <aside className="fixed inset-y-0 left-0 z-30 flex w-60 flex-col border-r border-white-regular bg-white">
        <NavLink to={PATH.adminHome} end className="block px-6 pb-4 pt-6">
          <img src={LogoIcon} alt="Learn It" className="h-6 w-auto" />
          <span className="mt-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-bold">
            Admin portal
          </span>
        </NavLink>
        <nav className="flex-1 overflow-y-auto px-3 pb-6">
          <NavLink
            to={PATH.adminHome}
            end
            className={({ isActive }) =>
              `mb-0.5 block rounded-lg px-3 py-2 text-sm transition-colors ${
                isActive
                  ? 'bg-blue-100 font-semibold text-blue-primary'
                  : 'text-gray-three hover:bg-grey-50 hover:text-gray-four'
              }`
            }
          >
            Overview
          </NavLink>
          {GROUPS.map((g) => (
            <div key={g.label} className="mt-4">
              <div className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-two">
                {g.label}
              </div>
              {g.items.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  className={({ isActive }) =>
                    `mb-0.5 block rounded-lg px-3 py-2 text-sm transition-colors ${
                      isActive
                        ? 'bg-blue-100 font-semibold text-blue-primary'
                        : 'text-gray-three hover:bg-grey-50 hover:text-gray-four'
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </div>
          ))}
          <div className="mt-6 px-3">
            <NavLink
              to={PATH.upcomingClasses}
              className="text-xs text-gray-two hover:text-blue-primary"
            >
              ← Back to your classes
            </NavLink>
          </div>
        </nav>
        <SidebarAccount
          user={user}
          subtitle={`${user.company?.name ?? '—'} · ${
            user.role === 'admin' ? 'Staff' : 'Manager'
          }`}
        />
      </aside>
      <main className="ml-60 min-h-screen">
        {user.role === 'admin' && <PreviewPicker />}
        <Outlet />
      </main>
    </div>
  );
};

export default CustomerAdminLayout;
